/**
 * Os comandos do escritório 2D num ecrã de toque.
 *
 * *"Precisamos adaptar agora o office2d pro mobile (…) que as mesmas coisas e
 * funções tanto do discord quanto do office 2d funcionem corretamente"*.
 *
 * No computador anda-se a WASD e usa-se o E, o T e o M. Num telemóvel não há
 * teclas, e é isso que estes comandos repõem, cada um no sítio do polegar:
 *   · o JOYSTICK, em baixo à esquerda, faz de WASD — escreve uma direcção num
 *     `ref` que o ciclo do Phaser lê a cada frame, como lê as teclas;
 *   · os botões CHAT e MAPA, em baixo à direita, fazem de T e de M;
 *   · o E não precisa de botão: os avisos da porta, do móvel e da escotilha já
 *     são botões, e tocar-lhes faz o mesmo que a tecla.
 * Tocar no chão também leva o boneco até lá (isso vive na cena, no Phaser).
 */
import { type PointerEvent as ReactPointerEvent, type RefObject, useEffect, useRef, useState } from "react";

import { IconChat, IconMap } from "./Icons";

export interface Direccao {
  x: number;
  y: number;
}

/** O raio do percurso do manípulo, em px. */
export const RAIO_DO_JOYSTICK = 44;
/** Abaixo desta fracção do raio o dedo está a descansar, não a andar. */
const ZONA_MORTA = 0.22;

/**
 * Do desvio do dedo (px, a partir do centro) para a direcção de andar e para o
 * sítio do manípulo. A direcção é unitária — o boneco anda sempre à mesma
 * velocidade, como com as teclas —, e é zero dentro da zona morta.
 */
export function lerJoystick(
  dx: number,
  dy: number,
  raio = RAIO_DO_JOYSTICK,
): { direccao: Direccao; manipulo: Direccao } {
  const d = Math.hypot(dx, dy);
  const k = d > raio ? raio / d : 1;
  const manipulo = { x: dx * k, y: dy * k };
  if (d < raio * ZONA_MORTA) return { direccao: { x: 0, y: 0 }, manipulo };
  return { direccao: { x: dx / d, y: dy / d }, manipulo };
}

interface Props {
  /** Onde a cena lê a direcção a cada frame. */
  direccaoRef: RefObject<Direccao>;
  onChat: () => void;
  onMapa: () => void;
}

export function OfficeTouchControls({ direccaoRef, onChat, onMapa }: Props) {
  const [manipulo, setManipulo] = useState<Direccao>({ x: 0, y: 0 });
  const centro = useRef<Direccao | null>(null);
  const dedo = useRef<number | null>(null);

  // Sair de cena com o dedo em cima (abriu-se o chat, a chamada tapou o mundo)
  // não pode deixar o boneco a andar sozinho.
  useEffect(() => {
    const alvo = direccaoRef.current;
    return () => {
      alvo.x = 0;
      alvo.y = 0;
    };
  }, [direccaoRef]);

  const mover = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (dedo.current !== e.pointerId || !centro.current) return;
    const lido = lerJoystick(e.clientX - centro.current.x, e.clientY - centro.current.y);
    direccaoRef.current.x = lido.direccao.x;
    direccaoRef.current.y = lido.direccao.y;
    setManipulo(lido.manipulo);
  };

  const largar = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (dedo.current !== e.pointerId) return;
    dedo.current = null;
    centro.current = null;
    direccaoRef.current.x = 0;
    direccaoRef.current.y = 0;
    setManipulo({ x: 0, y: 0 });
  };

  return (
    <>
      <div
        className="office-toque__stick"
        role="application"
        aria-label="Joystick: arrasta para andar"
        onPointerDown={(e) => {
          if (dedo.current !== null) return;
          const caixa = e.currentTarget.getBoundingClientRect();
          centro.current = { x: caixa.left + caixa.width / 2, y: caixa.top + caixa.height / 2 };
          dedo.current = e.pointerId;
          e.currentTarget.setPointerCapture?.(e.pointerId);
          mover(e);
        }}
        onPointerMove={mover}
        onPointerUp={largar}
        onPointerCancel={largar}
        onLostPointerCapture={largar}
      >
        <span className="office-toque__manipulo" style={{ transform: `translate(${manipulo.x}px, ${manipulo.y}px)` }} />
      </div>
      <div className="office-toque__botoes">
        <button type="button" className="office-toque__botao" onClick={onChat} aria-label="Abrir o chat do escritório">
          <IconChat size={20} />
          <span>Chat</span>
        </button>
        <button type="button" className="office-toque__botao" onClick={onMapa} aria-label="Abrir o mapa">
          <IconMap size={20} />
          <span>Mapa</span>
        </button>
      </div>
    </>
  );
}
